import React, { useRef } from 'react';
import { OtpBoxRow, OtpBox } from './SimpleLogin.styles';

const CODE_LENGTH = 6;

// A one-time code entered into individual boxes (same look as the sign-in page). `value` is the
// code typed so far as a plain string; `onChange` receives the new string. Typing a digit moves
// to the next box, Backspace on an empty box moves back, the arrow keys move between boxes, and
// a pasted code (or one autofilled into a box) is spread across the boxes, digits only.
export default function OtpCodeInput({ value = '', onChange, disabled = false, autoFocus = true, label = '6-digit code' }) {
  const refs = useRef([]);
  const digits = Array.from({ length: CODE_LENGTH }, (_, i) => value[i] || '');

  const focusBox = (index) => {
    refs.current[index]?.focus();
    refs.current[index]?.select();
  };

  const handleChange = (index, raw) => {
    const typed = String(raw).replace(/\D/g, '');
    const next = digits.slice();

    if (!typed) {
      // cleared this box (e.g. select-all + delete)
      next[index] = '';
      onChange(next.join(''));
      return;
    }

    // a single keystroke, or a whole code landing in one box
    let i = index;
    for (const digit of typed) {
      if (i >= CODE_LENGTH) break;
      next[i] = digit;
      i += 1;
    }
    onChange(next.join(''));
    focusBox(Math.min(i, CODE_LENGTH - 1));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      focusBox(index - 1);
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusBox(index - 1);
    } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      e.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (index, e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH - index);
    if (!pasted) return;
    e.preventDefault();
    handleChange(index, pasted);
  };

  return (
    <OtpBoxRow role="group" aria-label={label}>
      {digits.map((digit, index) => (
        <OtpBox
          key={index}
          ref={(el) => { refs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          autoFocus={autoFocus && index === 0}
          aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
          disabled={disabled}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </OtpBoxRow>
  );
}
